import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Shuffle } from "lucide-react";
import { getScoreColor } from "@/lib/scoreUtils";

interface RemixerData {
  remix_id: string;
  user_id: string;
  user_name: string;
  user_initials: string;
  remixed_score: number | null;
  created_at: string;
}

interface RemixersModalProps {
  isOpen: boolean;
  onClose: () => void;
  scoreId: string;
  totalCount: number;
  onViewDetail?: (remixId: string) => void;
}

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}


export function RemixersModal({ isOpen, onClose, scoreId, totalCount, onViewDetail }: RemixersModalProps) {
  const { membership } = useAuth();
  const [remixers, setRemixers] = useState<RemixerData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isOpen || !membership?.org_id) return;

    const fetchRemixers = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.rpc('get_prompt_remixes', {
          _score_id: scoreId,
          _org_id: membership.org_id,
        });

        if (error) {
          console.error('Error fetching remixers:', error);
          return;
        }

        setRemixers((data || []) as RemixerData[]);
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRemixers();
  }, [isOpen, scoreId, membership?.org_id]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md max-h-[70vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shuffle className="h-5 w-5 text-primary" />
            {totalCount} Remix{totalCount !== 1 ? 'es' : ''}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center justify-between py-2">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-4 w-12" />
                </div>
              ))}
            </div>
          ) : remixers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No remixes yet
            </div>
          ) : (
            <div className="space-y-1">
              {remixers.map((remixer) => (
                <button
                  key={remixer.remix_id}
                  onClick={() => onViewDetail?.(remixer.remix_id)}
                  className="w-full flex items-center justify-between py-3 px-2 -mx-2 rounded-lg hover:bg-muted/50 transition-colors text-left"
                >
                  <div className="text-sm text-muted-foreground">
                    {formatRelativeTime(remixer.created_at)}
                  </div>

                  {remixer.remixed_score !== null && (
                    <div className={`text-sm font-bold ${getScoreColor(remixer.remixed_score)}`}>
                      {remixer.remixed_score}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
